import { createContext, useContext, useMemo } from 'react';
import { resolveTheme, type ThemeView } from '@tallyframe/core';
import type { ReportBrand } from '@tallyframe/renderer';
import type { BrandSettings } from './api/types';

/**
 * The operator's brand, loaded once by `App.tsx` after sign-in and shared
 * down through the tree — the top bar reads it directly, and every screen
 * that renders a report maps it through `toReportBrand` for the renderer.
 */
export interface BrandState {
  brand: BrandSettings | null;
  reloadBrand(): Promise<void>;
  /** The stored theme and the plan's entitlement; `null` until loaded or on failure. */
  themeView: ThemeView | null;
  /** Call before sending a PUT; the token goes back into `applySavedTheme`. */
  beginThemeSave(): number;
  /** Applies a PUT response only if no load, clear or switch happened since the token. */
  applySavedTheme(result: ThemeView, token: number): void;
}

/** What a provider may supply: the theme members are optional so a test
 *  double with only `{ brand, reloadBrand }` still works. `useBrand` fills them. */
export type BrandContextValue = Pick<BrandState, 'brand' | 'reloadBrand'> &
  Partial<Omit<BrandState, 'brand' | 'reloadBrand'>>;

export const BrandContext = createContext<BrandContextValue>({
  brand: null,
  reloadBrand: async () => {}
});

const noToken = (): number => 0;
const noApply = (): void => {};

/** The brand and theme state. The theme members are no-ops (and `themeView`
 *  is null) when no provider, or a legacy test double, supplies them. */
export function useBrand(): BrandState {
  const value = useContext(BrandContext);
  return useMemo(
    () => ({
      ...value,
      themeView: value.themeView ?? null,
      beginThemeSave: value.beginThemeSave ?? noToken,
      applySavedTheme: value.applySavedTheme ?? noApply
    }),
    [value]
  );
}

/** `BrandSettings` (the API/persistence shape) plus the theme view to
 *  `ReportBrand` (the renderer's prop shape, spec §5.3). `undefined` — not an
 *  empty object — when there is no brand yet, so the renderer's own defaults
 *  apply. The theme is the *resolved* one, so the screen shows the same gated
 *  theme as the PDF; `theme` stays undefined until the view has loaded.
 *
 *  Free plans get a fresh theme object on every call, and the renderer
 *  memoizes on its identity, so callers must memoize this call on its inputs. */
export function toReportBrand(brand: BrandSettings | null, view: ThemeView | null): ReportBrand | undefined {
  if (!brand) return undefined;
  return {
    name: brand.displayName,
    accent: brand.accent,
    logoUrl: brand.logoUrl,
    theme: view ? resolveTheme(view.theme, { customTheme: view.entitled }) : undefined
  };
}
